import React, { useContext, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import TabletFrame from "./TabletFrame";
import PurchaseModal from "./PurchaseModal";
import { WizardFormData } from "../../types/wizard.types";
import { WizardFormField } from "../../machines/immersive-wizard.machine";
import {
  planDisplayMapping,
  appointmentPackageDisplayMapping,
} from "../../utils/membershipData";
import {
  getFormattedPrice,
  getSupportedCurrency,
} from "../../utils/currencyUtils";
import { AppContext } from "../../../../context/app";
import { TrendingUp } from "lucide-react";
import { gymMembers } from "../../utils/mockMembers";
import {
  MembershipPlan,
  AppointmentPackage,
  GymMember,
} from "../../types/wizard-demo.types";
import {
  getPlanFeatures,
  getSessionCount,
  getFallbackMembershipPlans,
  getFallbackAppointmentPackages,
} from "../../utils/membershipUtils";

interface PointOfSalesDemoProps {
  formData: WizardFormData;
}

const PointOfSalesDemo: React.FC<PointOfSalesDemoProps> = ({ formData }) => {
  const app = useContext(AppContext);
  const { client, user } = app;
  const { first_name, last_name } = user;
  const { currencyIso, company } = client;
  const currency = getSupportedCurrency(currencyIso);

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    item: MembershipPlan | AppointmentPackage | null;
    type: "membership" | "appointment";
  }>({
    isOpen: false,
    item: null,
    type: "membership",
  });

  const [showToast, setShowToast] = useState(false);
  const [purchasedItem, setPurchasedItem] = useState<{
    name: string;
    type: "membership" | "appointment";
    member: GymMember;
  } | null>(null);

  const [hasAnySales, setHasAnySales] = useState(false);
  const [animatedSales, setAnimatedSales] = useState(0);

  // Use ref to track current sales total for closures
  const salesRef = useRef(0);

  const getRandomMember = (): GymMember => {
    return gymMembers[Math.floor(Math.random() * gymMembers.length)];
  };

  const handlePurchase = async (
    item: MembershipPlan | AppointmentPackage,
    type: "membership" | "appointment",
    options: {
      toastDelay?: number;
      salesDelay?: number;
      toastDuration?: number;
    } = {},
  ) => {
    const {
      toastDelay = 800,
      salesDelay = 1000,
      toastDuration = 4000,
    } = options;

    // Wait before showing toast
    await new Promise((resolve) => setTimeout(resolve, toastDelay));

    setPurchasedItem({
      name: item.name,
      type,
      member: getRandomMember(),
    });
    setShowToast(true);

    // Wait then track sale
    await new Promise((resolve) => setTimeout(resolve, salesDelay));
    addSale(item);

    // Hide toast after duration
    await new Promise((resolve) => setTimeout(resolve, toastDuration));
    setShowToast(false);
    setPurchasedItem(null);
  };

  const parsePrice = (formattedPrice: string): number => {
    // Remove currency symbols and parse as float
    return parseFloat(formattedPrice.replace(/[^0-9.]/g, "")) || 0;
  };

  const addSale = (item: MembershipPlan | AppointmentPackage) => {
    const saleAmount = parsePrice(item.price);
    const previousTotal = salesRef.current;
    const newTotal = previousTotal + saleAmount;

    salesRef.current = newTotal;

    // Show sales section after first sale
    if (!hasAnySales) {
      setHasAnySales(true);
    }

    // Animate the number using the previous total as starting point
    animateNumber(previousTotal, newTotal);
  };

  const animateNumber = (from: number, to: number) => {
    const duration = 1200; // 1.2 seconds
    const startTime = Date.now();

    const updateNumber = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (easeOut)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = from + (to - from) * easeOut;

      setAnimatedSales(Math.round(currentValue));

      if (progress < 1) {
        requestAnimationFrame(updateNumber);
      }
    };

    requestAnimationFrame(updateNumber);
  };

  const openModal = (
    item: MembershipPlan | AppointmentPackage,
    type: "membership" | "appointment",
  ) => {
    setModalState({ isOpen: true, item, type });
  };

  const closeModal = () => {
    const currentItem = modalState.item;
    const currentType = modalState.type;

    // Close the modal first
    setModalState({ isOpen: false, item: null, type: "membership" });

    // If there was an item being viewed, handle the purchase flow
    if (currentItem) {
      handlePurchase(currentItem, currentType);
    }
  };

  const selectedPlans = formData?.[WizardFormField.MembershipPlans] || [];
  const selectedAppointmentPackages =
    formData?.[WizardFormField.SelectedAppointmentPackages] || [];

  // Check if user has selected anything at all
  const hasSelectedAnything =
    selectedPlans.length > 0 || selectedAppointmentPackages.length > 0;

  const membershipPlans: MembershipPlan[] =
    selectedPlans.length > 0
      ? (selectedPlans
          .map((planType, index) => {
            const planData =
              planDisplayMapping[planType as keyof typeof planDisplayMapping];
            if (!planData) return null;

            return {
              name: planData.name,
              price: getFormattedPrice(planData.pricing, currency),
              duration: "month",
              features: getPlanFeatures(planType),
              popular: index === 1, // Make the second plan popular
            };
          })
          .filter(Boolean) as MembershipPlan[])
      : hasSelectedAnything
        ? [] // If user selected something but not membership plans, show empty array
        : getFallbackMembershipPlans(currency); // Only show fallback if nothing selected at all

  const appointmentPackages: AppointmentPackage[] =
    selectedAppointmentPackages.length > 0
      ? (selectedAppointmentPackages
          .map((packageType) => {
            const packageData =
              appointmentPackageDisplayMapping[
                packageType as keyof typeof appointmentPackageDisplayMapping
              ];
            if (!packageData) return null;

            return {
              name: packageData.name,
              sessions: getSessionCount(packageType),
              price: getFormattedPrice(packageData.pricing, currency),
            };
          })
          .filter(Boolean) as AppointmentPackage[])
      : hasSelectedAnything
        ? [] // If user selected something but not appointment packages, show empty array
        : getFallbackAppointmentPackages(currency); // Only show fallback if nothing selected at all

  // Automatic demo animation - runs only once on mount
  useEffect(() => {
    const runDemoLoop = async () => {
      // Create a combined array of all available items for demo
      const demoItems: Array<{
        item: MembershipPlan | AppointmentPackage;
        type: "membership" | "appointment";
      }> = [];

      membershipPlans.forEach((plan) => {
        demoItems.push({ item: plan, type: "membership" });
      });

      appointmentPackages.forEach((pkg) => {
        demoItems.push({ item: pkg, type: "appointment" });
      });

      const numAutomaticPurchases = Math.min(2, demoItems.length);
      for (let i = 0; i < numAutomaticPurchases; i++) {
        const waitTime = i === 0 ? 2000 : 3000; // Slightly longer wait between items
        await new Promise((resolve) => setTimeout(resolve, waitTime));

        const demoItem = demoItems[i % demoItems.length];
        if (demoItem) {
          setModalState({
            isOpen: true,
            item: demoItem.item,
            type: demoItem.type,
          });

          // Close modal after 3 seconds
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setModalState({ isOpen: false, item: null, type: "membership" });

          // Handle purchase flow with custom timing for demo
          await handlePurchase(demoItem.item, demoItem.type, {
            toastDelay: 800,
            salesDelay: 1000,
            toastDuration: 3000,
          });
        }
      }
    };

    // Run demo if we have any items (membership plans OR appointment packages)
    if (membershipPlans.length > 0 || appointmentPackages.length > 0) {
      runDemoLoop();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  return (
    <TabletFrame brandColor={formData?.color || "#3B82F6"}>
      <div className="tw-h-full tw-bg-gray-50 tw-flex tw-flex-col">
        {/* Header */}
        <div
          className="tw-px-6 tw-py-4 tw-text-white tw-shadow-sm"
          style={{ backgroundColor: formData?.color || "#3B82F6" }}
        >
          <div className="tw-flex tw-items-center tw-justify-between">
            <div>
              <h1 className="tw-text-xl tw-font-semibold">
                {company || "Your Gym"}
              </h1>
            </div>
            <div className="tw-flex tw-items-center tw-space-x-4">
              <div className="tw-text-right">
                <p className="tw-text-lg tw-font-medium">
                  {first_name} {last_name}
                </p>
              </div>
              <div className="tw-w-12 tw-h-12 tw-bg-white/20 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                <span className="tw-text-lg tw-font-medium">
                  {first_name.charAt(0)}
                  {last_name.charAt(0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="tw-flex-1 tw-p-6 tw-overflow-auto tw-space-y-6">
          {/* Sales Tracking Section */}
          <AnimatePresence>
            {hasAnySales && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{
                  type: "spring",
                  duration: 0.6,
                  bounce: 0.3,
                }}
                className="tw-bg-gradient-to-r tw-from-green-50 tw-to-emerald-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-4"
              >
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-flex tw-items-center tw-space-x-3">
                    <div className="tw-w-10 tw-h-10 tw-bg-green-500 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                      <TrendingUp className="tw-text-white tw-text-lg" />
                    </div>
                    <div>
                      <h3 className="tw-text-lg tw-font-semibold tw-text-green-800">
                        Total Sales
                      </h3>
                    </div>
                  </div>
                  <div className="tw-text-right">
                    <motion.p
                      key={animatedSales}
                      initial={{ scale: 1.2, opacity: 0.7 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="tw-text-3xl tw-font-bold tw-text-green-700"
                    >
                      {getFormattedPrice(
                        {
                          USD: animatedSales,
                          AUD: Math.round(animatedSales * 1.5),
                          CAD: Math.round(animatedSales * 1.35),
                          GBP: Math.round(animatedSales * 0.8),
                        },
                        currency,
                      )}
                    </motion.p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Membership Plans Section */}
          {membershipPlans.length > 0 && (
            <div>
              <div className="tw-mb-4">
                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700">
                  Membership Plans
                </h3>
              </div>

              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                {membershipPlans.map((plan, index) => (
                  <div
                    key={index}
                    role="button"
                    tabIndex={0}
                    onClick={() => openModal(plan, "membership")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openModal(plan, "membership");
                      }
                    }}
                    className={`tw-bg-white tw-p-3 tw-rounded-lg tw-shadow-sm tw-border tw-transition-all tw-cursor-pointer tw-hover:tw-shadow-md tw-focus:tw-outline-none tw-focus:tw-ring-2 tw-focus:tw-ring-blue-500 ${
                      plan.popular
                        ? "tw-border-blue-500 tw-ring-1 tw-ring-blue-500/20"
                        : "tw-border-gray-200 tw-hover:tw-border-gray-300"
                    }`}
                  >
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <div className="tw-flex tw-items-center">
                        <h4 className="tw-text-base tw-font-semibold tw-text-gray-900">
                          {plan.name}
                        </h4>
                        {plan.popular && (
                          <span
                            className="tw-ml-2 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-white tw-rounded-full"
                            style={{
                              backgroundColor: formData?.color || "#3B82F6",
                            }}
                          >
                            Popular
                          </span>
                        )}
                      </div>
                      <div className="tw-text-right">
                        <p className="tw-text-xl tw-font-bold tw-text-gray-900">
                          {plan.price}
                        </p>
                        <p className="tw-text-xs tw-text-gray-500">
                          per {plan.duration}
                        </p>
                      </div>
                    </div>

                    <div className="tw-space-y-1">
                      {plan.features
                        .slice(0, 2)
                        .map((feature, featureIndex) => (
                          <div
                            key={featureIndex}
                            className="tw-flex tw-items-center"
                          >
                            <span className="tw-text-green-500 tw-mr-2">✓</span>
                            <span className="tw-text-xs tw-text-gray-600">
                              {feature}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Appointment Packages Section */}
          {appointmentPackages.length > 0 && (
            <div>
              <div className="tw-mb-4">
                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-700">
                  Appointment Packages
                </h3>
              </div>

              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                {appointmentPackages.map((pkg, index) => (
                  <div
                    key={index}
                    role="button"
                    tabIndex={0}
                    onClick={() => openModal(pkg, "appointment")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openModal(pkg, "appointment");
                      }
                    }}
                    className="tw-bg-white tw-p-3 tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-hover:tw-border-gray-300 tw-transition-all tw-cursor-pointer tw-hover:tw-shadow-md tw-focus:tw-outline-none tw-focus:tw-ring-2 tw-focus:tw-ring-blue-500"
                  >
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <div>
                        <h4 className="tw-text-base tw-font-semibold tw-text-gray-900">
                          {pkg.name}
                        </h4>
                        <p className="tw-text-xs tw-text-gray-600">
                          {pkg.sessions} session
                          {pkg.sessions !== "1" ? "s" : ""} included
                        </p>
                      </div>
                      <div className="tw-text-right">
                        <p className="tw-text-xl tw-font-bold tw-text-gray-900">
                          {pkg.price}
                        </p>
                      </div>
                    </div>

                    <div className="tw-space-y-1">
                      <div className="tw-flex tw-items-center">
                        <span className="tw-text-blue-500 tw-mr-2">📅</span>
                        <span className="tw-text-xs tw-text-gray-600">
                          Flexible scheduling
                        </span>
                      </div>
                      <div className="tw-flex tw-items-center">
                        <span className="tw-text-blue-500 tw-mr-2">🎯</span>
                        <span className="tw-text-xs tw-text-gray-600">
                          Personalized workout plans
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Purchase Modal */}
        <PurchaseModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          item={modalState.item}
          type={modalState.type}
          brandColor={formData?.color || "#3B82F6"}
        />

        {/* Custom Toast Notification */}
        <AnimatePresence>
          {showToast && purchasedItem && (
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.8 }}
              transition={{
                type: "spring",
                duration: 0.6,
                bounce: 0.3,
              }}
              className="tw-absolute tw-bottom-4 tw-right-4 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-p-3 tw-max-w-xs tw-z-40"
            >
              <div className="tw-flex tw-items-center tw-space-x-3">
                <div className="tw-flex-shrink-0">
                  <div className="tw-w-8 tw-h-8 tw-rounded-full tw-overflow-hidden tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
                    <img
                      src={purchasedItem.member.avatar}
                      alt={purchasedItem.member.fullName}
                      className="tw-w-full tw-h-full tw-object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `<span class="tw-text-sm tw-font-medium tw-text-blue-600">${purchasedItem.member.initials}</span>`;
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-text-neutral-800/80">
                    {purchasedItem.member.firstName} just purchased{" "}
                    <span className="tw-font-semibold">
                      {purchasedItem.name}
                    </span>
                    !
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </TabletFrame>
  );
};

export default PointOfSalesDemo;
